
import CampaignDetails, { CampaignDetailsFields } from "Components/campaigns/CampaignDetails";
import Loader from "Components/Common/Loader";
import Stepper from "Components/Common/Stepper";
import { PRIVILEGE } from "constants/privilege";
import { useFormik } from "formik";
import withAccessControl from "hoc/withAccessControl";
import { usePrivileges } from "hooks/useAuth";
import useDocumentTitle from "hooks/useDocumentTitle";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, CardBody, Form, Spinner } from "reactstrap";
import { createCampaign, updateCampaignDetails } from "services/api/campaign";
import { fetchCampaignDetails } from "slices/campaigns/details/thunk";
import { markCampaignDataExpired, refetchCampaignLists } from "slices/campaigns/reducer";
import { fetchUserBrands } from "slices/users/brands/thunk";
import { AppDispatch, RootState } from "store";
import { getErrorMessage } from "utils/helpers";
import * as Yup from "yup";

const steps = [
    { label: "Campaign Details", active: true },
    { label: "", active: false },
    { label: "", active: false },
    { label: "", active: false }
];


const CampaignDetailsPage = () => {

    useDocumentTitle("Campaign");

    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);

    const [loading, setLoading] = useState<boolean>(false);
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const privileges = usePrivileges();

    const { campaignId } = useParams();

    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns[campaignId!])
    const campaignLoading = useSelector((state: RootState) => state.campaignDetails.loading);
    const campaign = campaignDetails?.campaign ?? null;

    useEffect(() => {
        if(campaign && Number(campaign.version) >= 2){
            navigate(`/campaign/${campaign.uuid}/edit`)
        }
    }, [campaign])

    const defaultCampaignValues: CampaignDetailsFields = {
        name: campaignDetails?.campaign?.name ?? '',
        description: campaignDetails?.campaign?.description ?? '',
        brand_id: campaignDetails?.campaign?.brand_id ?? '',
        strategy: campaignDetails?.campaign?.strategy ?? 'paid',
        budget: campaignDetails?.campaign?.budget ?? '',
        total_budget: campaignDetails?.campaign?.total_budget ?? '',
        barter_budget: campaignDetails?.campaign?.barter_budget ?? '',
        brand: campaignDetails?.campaign?.brand ?? null,
        barter_reimbursement: campaignDetails?.campaign?.barter_reimbursement ?? false,
        landing_page_url: campaignDetails?.campaign?.landing_page_url ?? '',
        content_media_types: campaignDetails?.campaign?.content_media_types ?? { REELS: 1, FEED: 0, STORY: 0 },
        mode: campaignDetails?.campaign?.mode ?? 'workflow'
    }

    const formFields = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: defaultCampaignValues,
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Campaign name field can't be empty")
                .max(100, "Maximum 100 characters are allowed"),

            description: Yup.string()
                .required("Campaign description field can't be empty"),

            // brand_id: Yup.number()
            //   .required("Please select a brand"),

            // strategy: Yup.string()
            //     .required("Strategy is required"),

            // budget: Yup.number()
            //     .required("Please enter influencer budget amount (in INR)")
            //     .test(
            //         'is-less-than-total-budget',
            //         'Influencer budget should be less than Total Budget',
            //         function (value) {
            //             return value !== undefined && value !== null && value < (this.parent.total_budget ?? 0);
            //         }
            //     )
            //     .when('strategy', {
            //         is: 'paid',
            //         then: (schema) =>
            //             schema.min(1, 'Please enter valid influencer budget amount (in INR)'),
            //     }),

            // barter_budget: Yup.number()
            //   .notRequired(),

            total_budget: Yup.number()
                // .test(
                //     'is-greater-than-influencer-and-barter-budget',
                //     'Overall budget should be greater than Influencer + Barter budget',
                //     function (value) {
                //         const influencer = Number(this.parent.budget ?? 0);
                //         const barter = Number(this.parent.barter_budget ?? 0);
                //         return value !== undefined && value !== null && value >= influencer + barter;
                //     }
                // ),
            // content_media_types: Yup.object({
            //     REELS: Yup.number()
            //         .typeError('REELS must be a valid number.')
            //         .required('REELS is required.')
            //         .min(0, 'REELS must be at least 0.')
            //         .max(10, 'REELS must not be more than 10.'),
            //     FEED: Yup.number()
            //         .typeError('FEED must be a valid number.')
            //         .required('FEED is required.')
            //         .min(0, 'FEED must be at least 0.')
            //         .max(10, 'FEED must not be more than 10.'),
            //     STORY: Yup.number()
            //         .typeError('STORY must be a valid number.')
            //         .required('STORY is required.')
            //         .min(0, 'STORY must be at least 0.')
            //         .max(10, 'STORY must not be more than 10.'),
            // }).test(
            //     'at-least-one-min-one',
            //     'At least one of REELS, FEED, or STORY must be 1 or more.',
            //     (obj) => {
            //         return (obj?.REELS || 0) >= 1 || (obj?.FEED || 0) >= 1 || (obj?.STORY || 0) >= 1;
            //     }
            // )
        }),
        onSubmit: async (campaignData) => {
            try {
                if (!privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT))
                    throw new Error("You are not authorized to perform this action!");


                setLoading(true);
                const response = campaignId ? await updateCampaignDetails(selectedAgency?.uuid!, campaignId, campaignData) : await createCampaign(selectedAgency?.uuid!, campaignData);
                if (!response.campaign?.uuid) throw new Error("Failed to create new campaign..");
                dispatch(markCampaignDataExpired({ campaignId }));
                dispatch(refetchCampaignLists());
                if(campaignData.mode === 'offline'){
                    return navigate('/campaign/' + response.campaign.uuid);
                }
                navigate('/campaign/' + response.campaign.uuid + '/requirement');
            } catch (error) {
                toast.error(getErrorMessage(error, 'Failed to create new campaign'));
            } finally {
                setLoading(false)
            }
        }
    });

    useEffect(() => {
        dispatch(fetchUserBrands(false))
    }, [dispatch])

    useEffect(() => {
        if (campaignId)
            dispatch(fetchCampaignDetails(campaignId))
    }, [dispatch, campaignId])

    return (
        <div className="page-content">
            <div className="page-content-inner page-spacing">
                <div className="d-flex w-100 justify-content-between align-items-center pt-1 pb-5">
                    <Stepper steps={formFields.values.mode === 'offline' ? [steps[0]] : steps} />
                </div>
                {
                    campaignLoading ? (<Loader />) : (
                        <Form className='needs-validation' onSubmit={(e) => {
                            e.preventDefault();
                            formFields.handleSubmit();
                            return false;
                        }}>
                            <Card>
                                <CardBody>
                                    <CampaignDetails formFields={formFields} />
                                </CardBody>
                            </Card>
                            <div className="d-flex justify-content-between mb-4">
                                <Link to={{ pathname: '/campaign' }} type='button' className="theme-btn theme-btn-lg btn-primary-outlined"><i className='bx fs-4 bx-left-arrow-alt me-2' ></i>Back</Link>

                                <button type='submit' disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn d-flex justify-content-between align-items-center">
                                    <React.Fragment>
                                        {
                                            formFields.values.mode === 'offline' ? 'Save Campaign' : 'Proceed to Next'
                                        }
                                        {
                                            loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                                        }
                                    </React.Fragment>
                                </button>
                            </div>
                        </Form>
                    )
                }
            </div>
        </div>
    )
}

export default withAccessControl(CampaignDetailsPage, PRIVILEGE.CAMPAIGN.CAMPAIGN.CREATE)